import React, { useState, useEffect } from 'react'

import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import Alert from '@mui/material/Alert'
import UploadFileDropzone from './UploadFileDropzone'
import ImportResultsTable from './ImportResultsTable'

import Modal from 'components/common/Modal'
import useNotification from 'hooks/context/useNotification'
import useAuth from 'hooks/useAuth'

function ImportRecordsModal({ open, setOpen, uploadCsv, executeCsvImport }) {
  const [file, setFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [uploadResults, setUploadResults] = useState(null)
  const [uploadError, setUploadError] = useState(null)
  const [documentType, setDocumentType] = useState(null)
  const { setBasicNotification, setError } = useNotification()
  const { blockIdleTimer } = useAuth()

  // Parse CSV and validate document_type column
  const parseAndValidateDocumentType = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()

      reader.onload = (e) => {
        try {
          const text = e.target.result
          const lines = text.split(/\r?\n/).filter((line) => line.trim())

          if (lines.length < 2) {
            reject(
              new Error('CSV file must have at least a header and one data row')
            )
            return
          }

          // Parse header row
          const headerLine = lines[0]
          const headers = headerLine
            .split(',')
            .map((h) => h.trim().replace(/^"|"$/g, ''))

          // Find document_type column index
          const documentTypeIndex = headers.findIndex(
            (h) => h.toLowerCase() === 'document type'
          )

          if (documentTypeIndex === -1) {
            reject(new Error('Document Type column not found in CSV file'))
            return
          }

          // Extract all document_type values from data rows
          const documentTypes = []
          for (let i = 1; i < lines.length; i++) {
            const line = lines[i]
            if (!line.trim()) continue

            // Simple CSV parsing - handle quoted values
            const values = []
            let currentValue = ''
            let inQuotes = false

            for (let j = 0; j < line.length; j++) {
              const char = line[j]

              if (char === '"') {
                inQuotes = !inQuotes
              } else if (char === ',' && !inQuotes) {
                values.push(currentValue.trim().replace(/^"|"$/g, ''))
                currentValue = ''
              } else {
                currentValue += char
              }
            }
            values.push(currentValue.trim().replace(/^"|"$/g, ''))

            if (
              values[documentTypeIndex] !== undefined &&
              values[documentTypeIndex] !== ''
            ) {
              documentTypes.push(values[documentTypeIndex])
            }
          }

          if (documentTypes.length === 0) {
            reject(new Error('No document_type values found in CSV file'))
            return
          }

          // Check if all values are the same
          const firstValue = documentTypes[0]
          const allSame = documentTypes.every((value) => value === firstValue)

          if (allSame) {
            resolve(firstValue)
          } else {
            reject(
              new Error(
                'All document_type values must be the same in the CSV file'
              )
            )
          }
        } catch (error) {
          reject(error)
        }
      }

      reader.onerror = () => {
        reject(new Error('Error reading CSV file'))
      }

      reader.readAsText(file)
    })
  }

  const handleFileUpload = async (file) => {
    setUploading(true)
    setFile(file)
    setUploadError(null)
    setUploadResults(null)
    setDocumentType(null)

    try {
      // Validate document_type column before uploading
      const validatedDocumentType = await parseAndValidateDocumentType(file)
      setDocumentType(validatedDocumentType)

      const response = await uploadCsv(file, validatedDocumentType)

      if (
        !response.comparison.updates.length &&
        !response.comparison.inserts.length &&
        !response.comparison.errors.length
      ) {
        setUploadError('No changes found')
        setFile(null)
        setDocumentType(null)
        return
      }

      setUploadResults(response)
    } catch (error) {
      setUploadError(
        error.message ||
          error.display_message ||
          'An error occurred during upload'
      )
      setFile(null)
      setDocumentType(null)
    } finally {
      setUploading(false)
    }
  }

  useEffect(() => {
    if (!open) {
      setFile(null)
      setUploadResults(null)
      setUploadError(null)
      setDocumentType(null)
      blockIdleTimer(false)
    } else {
      blockIdleTimer(true)
    }
  }, [open])

  return (
    <Modal
      scrollable={true}
      height="75vh"
      width="80vw"
      open={open}
      onClose={() => {}}
      title={`Import Records`}
      hideButtonsBorder
      footerButtonProps={[
        uploadResults &&
          !uploadResults.comparison.errors?.length && {
            children: 'Confirm Import',
            variant: 'contained',
            color: 'primary',
            disabled: uploading,
            onClick: () => {
              setUploading(true)
              executeCsvImport(file, documentType)
                .then((result) => {
                  const updatesMade = result.execution.updated
                  const insertsMade = result.execution.inserted

                  setBasicNotification(
                    `Import successful. ${updatesMade} update${
                      updatesMade === 1 ? '' : 's'
                    } and ${insertsMade} insert${
                      insertsMade === 1 ? '' : 's'
                    } made.`
                  )
                  setOpen(false)
                })
                .catch((err) => {
                  setError(err.display_message)
                })
                .finally(() => {
                  setUploading(false)
                })
            },
          },
        {
          children: 'Close',
          variant: 'outlined',
          color: 'primary',
          disabled: uploading,
          onClick: () => {
            setOpen(false)
            // Reset state when modal closes
            setFile(null)
            setUploadResults(null)
            setUploadError(null)
            setDocumentType(null)
          },
        },
      ].filter(Boolean)}
    >
      <Box
        sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 3 }}
      >
        {/* File Upload Section */}
        {!uploading && !uploadResults && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Upload CSV File
            </Typography>
            {uploadError && (
              <Alert severity="error" sx={{ my: 2 }}>
                {uploadError}
              </Alert>
            )}
            <UploadFileDropzone
              file={file}
              onChange={handleFileUpload}
              disabled={uploading}
            />
          </Box>
        )}

        {/* Loading State */}
        {uploading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={48} sx={{ mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                Processing CSV file...
              </Typography>
            </Box>
          </Box>
        )}

        {/* Results Section */}
        {uploadResults && !uploading && (
          <Box>
            {uploadResults.comparison.errors?.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body1" fontWeight="medium">
                  Import cannot proceed due to validation errors. Please fix the
                  errors below before confirming the import.
                </Typography>
              </Alert>
            )}
            <ImportResultsTable comparisonData={uploadResults.comparison} />
          </Box>
        )}
      </Box>
    </Modal>
  )
}

export default ImportRecordsModal
